/**
 * M01 链路调试 fixture · W2-9 PR-2 数据工厂 v0
 *
 * 承接：PR-1 mock 基础设施 + CLAUDE.md §11.5.1 数据工厂
 *
 * 注册时机：apps/admin/src/main.tsx 启动时调用 registerM01Fixtures()
 *
 * 设计意图：
 * - 3 条不同 progression_state / final_outcome 的 mock session（覆盖 PM 验证 §3.1 正常路径多场景）
 * - trace_id 使用 session_id 直接复用（M01 SessionList 点击传 session_id 进 TraceDetail · 见 P9 §3）
 */
import { registerFixture } from "./registry";
import type { MockResponse } from "./types";

interface MockSession {
	session_id: string;
	merchant_id: string;
	customer_id: string;
	start_time: string;
	last_activity_time: string;
	session_status: string;
	intent: string | null;
	progression_state: string;
}

interface MockTrace {
	trace_id: string;
	session_id: string;
	merchant_id: string;
	customer_id: string | null;
	turn_index: number;
	timestamp: string;
	total_latency_ms: number;
	final_action_type: string;
	final_outcome: string;
	degraded_modules: string[];
	handoff_reason: string | null;
	reply_format: string;
	estimated_token_count: number;
	version_snapshot: {
		api_version: string;
		data_mode: string;
		runtime_capabilities: MockRuntimeCapabilities;
	};
	events: Array<
		| MockIntentEvent
		| MockPlaybookEvent
		| MockConversationEvent
		| { phase: string; duration_ms: number; summary?: Record<string, unknown> }
	>;
	eval_summary: MockTraceEvalSummary | null;
}

interface MockRuntimeCapabilities {
	phase4_runner_mode: string;
	prompt_effect_mode: string;
	rag_mode: string;
	session_mode: string;
	quality_mode: string;
	handoff_mode: string;
	shopify_read_mode: string;
	shopify_write_mode: string;
}

interface MockTraceEvalSummary {
	badcase_id: string | null;
	eval_run_id: string | null;
	sample_result_id: string | null;
	sample_id: string | null;
	dataset_id: string | null;
	dataset_version: string | null;
	status: string | null;
	severity: string | null;
	impact_target: string | null;
	root_cause_hint: string | null;
	responsible_asset: string | null;
	rerun_status: string | null;
	trace_id: string | null;
	admin_run_id: string | null;
	expected_intent: string | null;
	actual_intent: string | null;
	intent_match: boolean | null;
	expected_pb: string | null;
	actual_pb: string | null;
	pb_match: boolean | null;
	graduation_dataset_id: string | null;
	graduation_dataset_version: string | null;
	close_recommendation: "ready_to_close" | "needs_follow_up" | "not_ready";
	close_reason: string;
}

const NOW_ISO = new Date().toISOString();
const FIVE_MIN_AGO = new Date(Date.now() - 5 * 60_000).toISOString();
const ONE_HOUR_AGO = new Date(Date.now() - 60 * 60_000).toISOString();

const MOCK_SESSIONS: MockSession[] = [
	{
		session_id: "sess_mock_refund_001",
		merchant_id: "mer_devcons",
		customer_id: "cust_mock_anger_01",
		start_time: ONE_HOUR_AGO,
		last_activity_time: NOW_ISO,
		session_status: "active",
		intent: "refund",
		progression_state: "closing",
	},
	{
		session_id: "sess_mock_know_002",
		merchant_id: "mer_devcons",
		customer_id: "cust_mock_size_02",
		start_time: ONE_HOUR_AGO,
		last_activity_time: FIVE_MIN_AGO,
		session_status: "active",
		intent: "know",
		progression_state: "need_gathering",
	},
	{
		session_id: "sess_mock_handoff_003",
		merchant_id: "mer_devcons",
		customer_id: "cust_mock_complaint_03",
		start_time: ONE_HOUR_AGO,
		last_activity_time: ONE_HOUR_AGO,
		session_status: "closed",
		intent: "complaint",
		progression_state: "handoff",
	},
];

const MOCK_RUNTIME_CAPABILITIES: MockRuntimeCapabilities = {
	phase4_runner_mode: "llm",
	prompt_effect_mode: "consumed_by_llm",
	rag_mode: "qdrant_live",
	session_mode: "pg_session",
	quality_mode: "q_fast_grounding_starter",
	handoff_mode: "contract_only",
	shopify_read_mode: "live_read",
	shopify_write_mode: "dry_run",
};

interface MockIntentEvent {
	phase: "intent_recognition";
	intent: string;
	service_stage: string;
	confidence: number;
	topic: string | null;
	implementation_level: "L1" | "L2" | "fused";
	l2_matched_rule_id: string | null;
	secondary_intent: string | null;
	intent_continuity: string;
}

interface MockPlaybookEvent {
	phase: "playbook_recommendation";
	playbook_id: string | null;
	playbook_name: string | null;
	playbook_version: string | null;
	recommended_skills: string[];
	universal_skills: string[];
	match_mode: string;
}

interface MockConversationEvent {
	phase: "conversation_io";
	user_message: string;
	assistant_reply: string;
	history_turns_included: number;
	session_mode: string;
	handoff_mode: string;
	session_status: string;
	progression_state: string;
}

function intentEventFor(s: MockSession): MockIntentEvent {
	if (s.intent === "refund") {
		return {
			phase: "intent_recognition",
			intent: "refund_request",
			service_stage: "after_sale",
			confidence: 0.92,
			topic: "申请退款",
			implementation_level: "L2",
			l2_matched_rule_id: "R012_refund",
			secondary_intent: null,
			intent_continuity: "switch",
		};
	}
	if (s.intent === "complaint") {
		return {
			phase: "intent_recognition",
			intent: "human_handoff",
			service_stage: "after_sale",
			confidence: 0.88,
			topic: "客户要求转人工",
			implementation_level: "L2",
			l2_matched_rule_id: "R051_handoff",
			secondary_intent: null,
			intent_continuity: "switch",
		};
	}
	return {
		phase: "intent_recognition",
		intent: "knowledge_qa",
		service_stage: "in_sale",
		confidence: 0.71,
		topic: "尺码询问",
		implementation_level: "L1",
		l2_matched_rule_id: null,
		secondary_intent: "product_inquiry",
		intent_continuity: "continue",
	};
}

function playbookEventFor(s: MockSession): MockPlaybookEvent {
	if (s.intent === "refund") {
		return {
			phase: "playbook_recommendation",
			playbook_id: "PB02_refund",
			playbook_name: "退款处理",
			playbook_version: "1.0.0",
			recommended_skills: ["SK02_lookup_order", "SK14_refund_initiate"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (s.intent === "complaint") {
		return {
			phase: "playbook_recommendation",
			playbook_id: null,
			playbook_name: null,
			playbook_version: null,
			recommended_skills: [],
			universal_skills: ["SK05", "SK11"],
			match_mode: "no_match",
		};
	}
	return {
		phase: "playbook_recommendation",
		playbook_id: null,
		playbook_name: null,
		playbook_version: null,
		recommended_skills: [],
		universal_skills: ["SK05", "SK11"],
		match_mode: "no_match",
	};
}

function conversationEventFor(s: MockSession): MockConversationEvent {
	if (s.intent === "refund") {
		return {
			phase: "conversation_io",
			user_message: "我想申请退款，订单号 1001。",
			assistant_reply: "已根据订单状态进入退款处理流程。",
			history_turns_included: 0,
			session_mode: "pg_session",
			handoff_mode: "contract_only",
			session_status: s.session_status,
			progression_state: s.progression_state,
		};
	}
	if (s.intent === "complaint") {
		return {
			phase: "conversation_io",
			user_message: "我已经等很久了，帮我转人工。",
			assistant_reply: "我会为您预留人工转接，并保留本次问题和处理记录。",
			history_turns_included: 1,
			session_mode: "pg_session",
			handoff_mode: "contract_only",
			session_status: s.session_status,
			progression_state: s.progression_state,
		};
	}
	return {
		phase: "conversation_io",
		user_message: "这件衣服尺码怎么选？",
		assistant_reply: "我可以先按商品尺码表帮您判断，也可以结合身高体重继续确认。",
		history_turns_included: 0,
		session_mode: "pg_session",
		handoff_mode: "contract_only",
		session_status: s.session_status,
		progression_state: s.progression_state,
	};
}

const MOCK_TRACES: Record<string, MockTrace> = Object.fromEntries(
	MOCK_SESSIONS.map((s) => [
		s.session_id,
		{
			trace_id: s.session_id,
			session_id: s.session_id,
			merchant_id: s.merchant_id,
			customer_id: s.customer_id,
			turn_index: 0,
			timestamp: s.last_activity_time,
			total_latency_ms: s.intent === "refund" ? 1450 : 850,
			final_action_type: s.progression_state === "handoff" ? "handoff" : "inform",
			final_outcome:
				s.progression_state === "handoff"
					? "handoff"
					: s.progression_state === "closing"
						? "success"
						: "partial",
			degraded_modules: [],
			handoff_reason: s.progression_state === "handoff" ? "policy_disagreement" : null,
			reply_format: "text",
			estimated_token_count: 240,
			version_snapshot: {
				api_version: "2026-04",
				data_mode: "mock",
				runtime_capabilities: MOCK_RUNTIME_CAPABILITIES,
			},
			events: [
				intentEventFor(s),
				playbookEventFor(s),
				{ phase: "Phase1.refine", duration_ms: 50 },
				{ phase: "Phase2.retrieval", duration_ms: 180 },
				{ phase: "Phase3.rerank", duration_ms: 60 },
				{
					phase: "phase4_generate",
					duration_ms: 540,
					summary: {
						action_type: s.progression_state === "handoff" ? "escalate" : "inform",
						runner_error: null,
						executed_action_count: s.intent === "refund" ? 1 : 0,
						executed_actions:
							s.intent === "refund"
								? [{ skill_id: "SK02", tool_id: "lookup_order", status: "success" }]
								: [],
						loop_meta: {
							steps_used: s.intent === "refund" ? 1 : 0,
							budget_exhausted: false,
							dedup_hit_rate: 0,
						},
					},
				},
				{
					phase: "phase5_validate",
					duration_ms: 20,
					summary: {
						decision: "PASS",
						grounding_check: {
							triggered: true,
							passed: true,
							unsupported_claims: [],
							grounded_ratio: 0.96,
							reason: null,
						},
						side_effect_check: { triggered: true, passed: true, issues: [] },
						step_used: s.intent === "refund" ? 1 : 0,
						step_budget: 4,
						terminated_by: "natural",
					},
				},
				conversationEventFor(s),
			],
			eval_summary: s.intent === "refund" ? evalSummaryForRefundTrace(s) : null,
		},
	]),
);

function evalSummaryForRefundTrace(s: MockSession): MockTraceEvalSummary {
	return {
		badcase_id: "bc_m01_refund_001",
		eval_run_id: "eval_run_m01_refund_001",
		sample_result_id: "sr_m01_refund_001",
		sample_id: "S003",
		dataset_id: "golden15_representative",
		dataset_version: "2026-05-08",
		status: "fixed",
		severity: "P1",
		impact_target: "退款回复准确率",
		root_cause_hint: "playbook_mismatch",
		responsible_asset: "PB02_refund",
		rerun_status: "rerun_passed",
		trace_id: s.session_id,
		admin_run_id: `run_${s.session_id}`,
		expected_intent: "refund_request",
		actual_intent: "refund_request",
		intent_match: true,
		expected_pb: "PB02_refund",
		actual_pb: "PB02_refund",
		pb_match: true,
		graduation_dataset_id: null,
		graduation_dataset_version: null,
		close_recommendation: "ready_to_close",
		close_reason: "复跑通过，建议关闭或写回回归集。",
	};
}

/**
 * 注册 M01 fixtures · 启动时调用一次
 */
export function registerM01Fixtures(): void {
	// /admin/v1/health/degradation · M13 降级率仪表盘（W6）
	registerFixture({
		pathname: "/admin/v1/health/degradation",
		methods: ["GET"],
		resolve: (ctx) => {
			const windowHours = Number(ctx.query.window_hours ?? 24);
			const threshold = Number(ctx.query.threshold ?? 0.2);
			const rate = 0.33;
			return {
				data: {
					window_hours: windowHours,
					total_traces: 12,
					degraded_traces: 4,
					degradation_rate: rate,
					handoff_rate: 0.17,
					by_module: [
						{ module: "main_agent", count: 3 },
						{ module: "rag", count: 2 },
					],
					by_outcome: { success: 6, partial: 4, handoff: 2 },
					threshold,
					alert: rate > threshold,
					since: "2026-06-26T00:00:00Z",
					until: "2026-06-26T12:00:00Z",
				},
			};
		},
	});

	// /admin/v1/sessions/recent · 返回 3 条 mock session
	registerFixture({
		pathname: "/admin/v1/sessions/recent",
		methods: ["GET"],
		resolve: () => ({
			data: { items: MOCK_SESSIONS, total: MOCK_SESSIONS.length },
		}),
	});

	// /admin/v1/trace · trace 列表/检索（W4）
	registerFixture({
		pathname: "/admin/v1/trace",
		methods: ["GET"],
		resolve: (ctx) => {
			const all = Object.values(MOCK_TRACES).map((t) => ({
				trace_id: t.trace_id,
				session_id: t.session_id,
				merchant_id: t.merchant_id,
				timestamp: t.timestamp,
				turn_index: t.turn_index,
				intent:
					(
						t.events.find((e) => e.phase === "intent_recognition") as
							| { intent?: string }
							| undefined
					)?.intent ?? null,
				final_action_type: t.final_action_type,
				final_outcome: t.final_outcome,
				degraded_modules: t.degraded_modules,
				handoff_reason: t.handoff_reason,
				total_latency_ms: t.total_latency_ms,
			}));
			const outcome = ctx.query.final_outcome;
			const degradedOnly = ctx.query.degraded_only === "true";
			const sessionId = ctx.query.session_id;
			const items = all.filter(
				(it) =>
					(!outcome || it.final_outcome === outcome) &&
					(!degradedOnly || it.degraded_modules.length > 0) &&
					(!sessionId || it.session_id === sessionId),
			);
			return { data: { items, total: items.length, limit: 50, offset: 0 } };
		},
	});

	// /admin/v1/trace/:trace_id · 按 session_id 取对应 trace
	registerFixture<MockTrace | { detail: string }>({
		pathname: "/admin/v1/trace/:trace_id",
		methods: ["GET"],
		resolve: (ctx): MockResponse<MockTrace | { detail: string }> => {
			const traceId = ctx.params.trace_id;
			const trace = traceId ? MOCK_TRACES[traceId] : undefined;
			if (!trace) {
				return {
					status: 404,
					data: { detail: `mock fixture: trace ${traceId} not found` },
				};
			}
			return { data: trace };
		},
	});
}
